import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  FlashOn,
  Login,
  Logout,
  MoreVert,
  Person,
  Phone,
  Search,
  Sync,
  ViewList,
  ViewModule,
  VpnKey,
  Group,
  History
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { dataverseService } from '../services/dataverseService';
import PhoneKeysDialog from '../components/PhoneKeysDialog';
import AddToAccessGroupDialog from '../components/AddToAccessGroupDialog';
import {
  KEY_REQUEST_ACTION,
  KEY_REQUEST_DURATION_DEFAULT,
  KEY_REQUEST_SCOPE
} from '../config/keyRequest';
import {
  getDataverseErrorMessage,
  handleDataverseLoadError
} from '../lib/dataverseErrorHandler';
import { normalizeMobileNumber } from '../config/keyRequest';
import {
  isManualPhoneLogoffEnabled,
  isManualPhoneLogonEnabled
} from '../config/features';

const emptyForm = {
  name: '',
  mobileNumber: '',
  blaalys: false,
  lagreNokler: false
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Ikke registrert';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('nb-NO', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(date);
};

const getLoggedInUserLabel = (phone) =>
  phone.lastName || phone.lastEmail || phone.lastUserId || 'Ukjent bruker';

function PhoneStatusChips({ phone, groups = [] }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      <Chip
        size="small"
        color={phone.isLoggedIn ? 'success' : 'default'}
        label={phone.isLoggedIn ? 'Pålogget' : 'Ikke pålogget'}
      />
      {phone.isLoggedIn && (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<Person />}
          label={getLoggedInUserLabel(phone)}
        />
      )}
      {phone.blaalys && (
        <Chip size="small" color="error" icon={<FlashOn />} label="Blålys" />
      )}
      {phone.lagreNokler && (
        <Chip size="small" color="primary" icon={<VpnKey />} label="Lagre nøkler" />
      )}
      {groups.map((group) => (
        <Chip
          key={group.id}
          size="small"
          icon={<Group />}
          label={group.name}
          variant="outlined"
        />
      ))}
    </Box>
  );
}

function PhoneCard({
  phone,
  groups = [],
  blaalysSubmitting,
  onOpenMenu,
  onShowKeys,
  onViewLog,
  onViewUserLog,
  onBlaalys
}) {
  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        borderColor: phone.isLoggedIn ? theme.palette.success.main : undefined,
        backgroundColor: phone.isLoggedIn
          ? alpha(theme.palette.success.main, 0.06)
          : undefined
      })}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Phone color={phone.isLoggedIn ? 'success' : 'primary'} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {phone.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {phone.mobileNumber}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <PhoneStatusChips phone={phone} groups={groups} />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                {phone.isLoggedIn ? (
                  <>
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      Pålogget som {getLoggedInUserLabel(phone)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Innlogget: {formatDateTime(phone.lastLogon)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Siste utlogging: {formatDateTime(phone.lastLogoff)}
                  </Typography>
                )}
                {phone.lastDeviceId && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Enhet: {phone.lastDeviceId}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <IconButton
            aria-label={`Handlinger for telefon ${phone.mobileNumber || phone.name || ''}`.trim()}
            onClick={(event) => onOpenMenu(event, phone)}
          >
            <MoreVert />
          </IconButton>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          startIcon={<VpnKey />}
          onClick={() => onShowKeys(phone)}
        >
          Vis nøkler
        </Button>
        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 1 }}
          startIcon={<History />}
          onClick={() => onViewLog(phone)}
        >
          Logg
        </Button>
        {phone.isLoggedIn && (phone.lastUserId || phone.lastEmail || phone.lastName) && (
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 1 }}
            startIcon={<Person />}
            onClick={() => onViewUserLog(phone)}
          >
            Logg for pålogget bruker
          </Button>
        )}

        {phone.blaalys && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            sx={{ mt: 1 }}
            startIcon={
              blaalysSubmitting === phone.phoneId ? (
                <CircularProgress size={18} />
              ) : (
                <FlashOn />
              )
            }
            disabled={blaalysSubmitting === phone.phoneId}
            onClick={() => onBlaalys(phone)}
          >
            Blålys – alle låser
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function PhoneRow({ phone, groups = [], onOpenMenu, onShowKeys, onViewLog }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        borderColor: phone.isLoggedIn ? 'success.main' : undefined
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200, flex: '1 1 200px' }}>
        <Phone color={phone.isLoggedIn ? 'success' : 'primary'} fontSize="small" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {phone.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {phone.mobileNumber}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: '2 1 220px', minWidth: 160 }}>
        <PhoneStatusChips phone={phone} groups={groups} />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 140px', minWidth: 120 }}>
        {phone.isLoggedIn
          ? `Innlogget: ${formatDateTime(phone.lastLogon)}`
          : `Utlogging: ${formatDateTime(phone.lastLogoff)}`}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, ml: 'auto', alignItems: 'center' }}>
        <Button size="small" startIcon={<VpnKey />} onClick={() => onShowKeys(phone)}>
          Nøkler
        </Button>
        <Button size="small" startIcon={<History />} onClick={() => onViewLog(phone)}>
          Logg
        </Button>
        <IconButton
          size="small"
          aria-label={`Handlinger for telefon ${phone.mobileNumber || phone.name || ''}`.trim()}
          onClick={(event) => onOpenMenu(event, phone)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

const PhonesPage = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPhone, setMenuPhone] = useState(null);
  const [blaalysSubmitting, setBlaalysSubmitting] = useState(null);
  const [keysPhone, setKeysPhone] = useState(null);
  const [groupsByPhoneId, setGroupsByPhoneId] = useState({});
  const [addGroupPhone, setAddGroupPhone] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncingPhoneId, setSyncingPhoneId] = useState(null);
  const [sessionSubmitting, setSessionSubmitting] = useState(null);
  const [logonDialogPhone, setLogonDialogPhone] = useState(null);
  const [logonUserName, setLogonUserName] = useState('');
  const [logonUserError, setLogonUserError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  /** '' | 'loggedIn' | 'loggedOut' */
  const [loginFilter, setLoginFilter] = useState('');

  const loadPhones = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const data = await dataverseService.phoneService.getAll();
      setPhones(data);

      try {
        const groups = await dataverseService.accessGroupService.getAll();
        const withMembers = await Promise.all(
          groups.map((group) => dataverseService.accessGroupService.getById(group.id))
        );
        const map = {};
        for (const group of withMembers) {
          for (const phone of group.phones) {
            if (!map[phone.id]) {
              map[phone.id] = [];
            }
            map[phone.id].push(group);
          }
        }
        setGroupsByPhoneId(map);
      } catch (groupError) {
        console.warn('Could not load access groups for phones:', groupError);
        setGroupsByPhoneId({});
      }
    } catch (loadError) {
      console.error('Error loading phones:', loadError);
      if (handleDataverseLoadError(loadError, navigate)) {
        return;
      }
      setPhones([]);
      setError(getDataverseErrorMessage(loadError));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadPhones();
  }, [loadPhones]);

  const openCreateDialog = () => {
    setEditingPhone(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (phone) => {
    setEditingPhone(phone);
    setFormData({
      name: phone.name,
      mobileNumber: phone.mobileNumber,
      blaalys: phone.blaalys,
      lagreNokler: phone.lagreNokler
    });
    setDialogOpen(true);
    setMenuAnchor(null);
    setMenuPhone(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Navn er påkrevd.');
      return;
    }

    if (!normalizeMobileNumber(formData.mobileNumber)) {
      setError('Oppgi et gyldig norsk mobilnummer.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingPhone) {
        await dataverseService.phoneService.update(editingPhone.phoneId, formData);
      } else {
        const created = await dataverseService.phoneService.create(formData);
        try {
          await dataverseService.accessGroupService.enrollNewPhoneInDefaultGroup(
            created.phoneId,
            created.mobileNumber
          );
        } catch (enrollError) {
          console.warn('Default access group enrollment failed:', enrollError);
        }
      }
      setDialogOpen(false);
      await loadPhones();
    } catch (saveError) {
      setError(getDataverseErrorMessage(saveError, 'Kunne ikke lagre telefon.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFromUnloc = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    setSyncError(null);
    setError(null);
    setSyncMessage('Starter synk fra Unloc…');

    try {
      setSyncMessage('Henter telefoner fra Unloc…');
      const summary = await dataverseService.phoneService.importFromUnloc();
      setSyncMessage(
        `Oppdatert fra Unloc: ${summary.imported} nye, ${summary.reactivated} reaktivert, ${summary.updated} oppdatert.`
      );
      await loadPhones({ showLoading: false });
    } catch (importError) {
      console.error('Phone sync failed:', importError);
      setSyncError(getDataverseErrorMessage(importError, 'Kunne ikke oppdatere telefoner fra Unloc.'));
      setSyncMessage(null);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncPhoneFromUnloc = async (phone) => {
    setSyncingPhoneId(phone.phoneId);
    setSyncError(null);
    setError(null);
    setSyncMessage(`Henter ${phone.name || phone.mobileNumber} fra Unloc…`);
    try {
      await dataverseService.phoneService.syncFromUnloc(phone.phoneId);
      setSyncMessage(`Oppdatert ${phone.name || phone.mobileNumber} fra Unloc`);
      await loadPhones({ showLoading: false });
    } catch (phoneSyncError) {
      setSyncError(getDataverseErrorMessage(phoneSyncError, 'Kunne ikke synce telefon fra Unloc.'));
      setSyncMessage(null);
    } finally {
      setSyncingPhoneId(null);
      setMenuAnchor(null);
      setMenuPhone(null);
    }
  };

  const handleDeactivate = async (phone) => {
    const confirmed = window.confirm(`Deaktivere ${phone.name} (${phone.mobileNumber})?`);
    if (!confirmed) {
      return;
    }

    setMenuAnchor(null);
    setMenuPhone(null);

    try {
      await dataverseService.phoneService.deactivate(phone.phoneId);
      await loadPhones();
    } catch (deactivateError) {
      setError(getDataverseErrorMessage(deactivateError, 'Kunne ikke deaktivere telefon.'));
    }
  };

  const handleBlaalys = async (phone) => {
    const confirmed = window.confirm(
      `Gir nøkler til alle Unloc-låser for ${phone.mobileNumber}. Fortsette?`
    );
    if (!confirmed) {
      return;
    }

    setBlaalysSubmitting(phone.phoneId);
    setError(null);

    try {
      const created = await dataverseService.keyRequestService.create({
        action: KEY_REQUEST_ACTION.GRANT,
        scope: KEY_REQUEST_SCOPE.ALL_LOCKS,
        mobileNumber: phone.mobileNumber,
        phoneId: phone.phoneId,
        durationMinutes: KEY_REQUEST_DURATION_DEFAULT,
        contextLabel: phone.name
      });

      const result = await dataverseService.keyRequestService.waitForCompletion(created.requestId);
      const count = result.locksCount ?? 0;
      window.alert(`Blålys: nøkler utstedt til ${phone.mobileNumber} for ${count} lås(er).`);
    } catch (blaalysError) {
      setError(getDataverseErrorMessage(blaalysError, 'Blålys-handling feilet.'));
    } finally {
      setBlaalysSubmitting(null);
    }
  };

  const openManualLogonDialog = (phone) => {
    setMenuAnchor(null);
    setMenuPhone(null);
    setLogonDialogPhone(phone);
    setLogonUserName('');
    setLogonUserError('');
  };

  const closeManualLogonDialog = () => {
    if (sessionSubmitting) {
      return;
    }
    setLogonDialogPhone(null);
    setLogonUserName('');
    setLogonUserError('');
  };

  const handleManualLogonConfirm = async () => {
    const phone = logonDialogPhone;
    if (!phone) {
      return;
    }

    const sessionUserName = logonUserName.trim();
    if (!sessionUserName) {
      setLogonUserError('Oppgi navn på pålogget bruker.');
      return;
    }

    setLogonUserError('');
    setSessionSubmitting(phone.phoneId);
    setError(null);
    setSyncMessage(`Logger på ${phone.name || phone.mobileNumber}…`);

    try {
      await dataverseService.phoneSessionService.logon(phone, {
        account: accounts[0],
        sessionUserName
      });
      setLogonDialogPhone(null);
      setLogonUserName('');
      setSyncMessage(
        `${phone.name || phone.mobileNumber} er markert som pålogget (${sessionUserName}).`
      );
      await loadPhones({ showLoading: false });
    } catch (logonError) {
      console.error('Manual phone logon failed:', logonError);
      setSyncMessage(null);
      setError(getDataverseErrorMessage(logonError, 'Kunne ikke logge på telefonen.'));
    } finally {
      setSessionSubmitting(null);
    }
  };

  const handleManualLogoff = async (phone) => {
    const confirmed = window.confirm(
      `Logge av ${phone.name} (${phone.mobileNumber}) fra OpenCare?\n\n` +
        'Samme som mobil utlogging / auto-logout: fjernes fra alle tilgangsgrupper og direkte nøkler trekkes tilbake.'
    );
    if (!confirmed) {
      return;
    }

    setMenuAnchor(null);
    setMenuPhone(null);
    setSessionSubmitting(phone.phoneId);
    setError(null);
    setSyncMessage(`Logger av ${phone.name || phone.mobileNumber}…`);

    try {
      await dataverseService.phoneSessionService.logoff(phone, { account: accounts[0] });
      setSyncMessage(`${phone.name || phone.mobileNumber} er markert som avlogget.`);
      await loadPhones({ showLoading: false });
    } catch (logoffError) {
      console.error('Manual phone logoff failed:', logoffError);
      setSyncMessage(null);
      setError(getDataverseErrorMessage(logoffError, 'Kunne ikke logge av telefonen.'));
    } finally {
      setSessionSubmitting(null);
    }
  };

  const loggedInCount = useMemo(
    () => phones.filter((phone) => phone.isLoggedIn).length,
    [phones]
  );
  const loggedOutCount = phones.length - loggedInCount;

  const filteredPhones = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const matched = phones.filter((phone) => {
      if (loginFilter === 'loggedIn' && !phone.isLoggedIn) {
        return false;
      }
      if (loginFilter === 'loggedOut' && phone.isLoggedIn) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }

      const groupNames = (groupsByPhoneId[phone.phoneId] || [])
        .map((group) => group.name)
        .filter(Boolean);
      const searchFields = [
        phone.name,
        phone.mobileNumber,
        phone.lastName,
        phone.lastEmail,
        phone.lastUserId,
        ...groupNames
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      return searchFields.some((value) => value.includes(normalizedSearch));
    });

    return matched.sort((left, right) => {
      if (loginFilter === 'loggedIn') {
        const leftTs = left.lastLogon ? new Date(left.lastLogon).getTime() : 0;
        const rightTs = right.lastLogon ? new Date(right.lastLogon).getTime() : 0;
        if (leftTs !== rightTs) {
          return rightTs - leftTs;
        }
      } else if (left.isLoggedIn !== right.isLoggedIn) {
        return left.isLoggedIn ? -1 : 1;
      }

      return (left.name || left.mobileNumber || '').localeCompare(
        right.name || right.mobileNumber || '',
        'nb-NO'
      );
    });
  }, [phones, searchTerm, loginFilter, groupsByPhoneId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Telefoner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kun registrerte telefoner kan få eller miste nøkler.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <Sync />}
            onClick={handleSyncFromUnloc}
            disabled={syncing || saving || loading}
          >
            {syncing ? 'Oppdaterer…' : 'Oppdater fra Unloc'}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            Ny telefon
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, next) => {
              if (next) setViewMode(next);
            }}
          >
            <ToggleButton value="list">
              <ViewList sx={{ mr: 0.75 }} />
              Liste
            </ToggleButton>
            <ToggleButton value="tiles">
              <ViewModule sx={{ mr: 0.75 }} />
              Kort
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {syncError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncError(null)}>
          {syncError}
        </Alert>
      )}

      {syncMessage && !syncError && (
        <Alert
          severity={syncing || syncingPhoneId ? 'info' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => !syncing && !syncingPhoneId && setSyncMessage(null)}
        >
          {syncMessage}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Nye telefoner opprettes også som brukere i Unloc. Endringer gjort direkte i Unloc hentes med
        «Oppdater fra Unloc» eller «Hent fra Unloc» på en telefon.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Søk etter navn, mobilnummer, bruker eller gruppe..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  clickable
                  color="success"
                  variant={loginFilter === 'loggedIn' ? 'filled' : 'outlined'}
                  label={`${loggedInCount} pålogget`}
                  onClick={() =>
                    setLoginFilter((current) => (current === 'loggedIn' ? '' : 'loggedIn'))
                  }
                />
                <Chip
                  clickable
                  variant={loginFilter === 'loggedOut' ? 'filled' : 'outlined'}
                  color={loginFilter === 'loggedOut' ? 'primary' : 'default'}
                  label={`${loggedOutCount} ikke pålogget`}
                  onClick={() =>
                    setLoginFilter((current) => (current === 'loggedOut' ? '' : 'loggedOut'))
                  }
                />
                <Chip
                  label={`${filteredPhones.length} av ${phones.length}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {phones.length === 0 ? (
        <Alert severity="info">Ingen aktive telefoner registrert.</Alert>
      ) : filteredPhones.length === 0 ? (
        <Alert severity="info">Ingen telefoner matcher søket eller filteret.</Alert>
      ) : viewMode === 'tiles' ? (
        <Grid container spacing={2}>
          {filteredPhones.map((phone) => (
            <Grid item xs={12} md={6} lg={4} key={phone.phoneId}>
              <PhoneCard
                phone={phone}
                groups={groupsByPhoneId[phone.phoneId] || []}
                blaalysSubmitting={blaalysSubmitting}
                onOpenMenu={(event, selected) => {
                  setMenuAnchor(event.currentTarget);
                  setMenuPhone(selected);
                }}
                onShowKeys={setKeysPhone}
                onViewLog={(selected) =>
                  navigate(
                    `/reports?report=hendelseslogg&phone=${encodeURIComponent(selected.mobileNumber || '')}`
                  )
                }
                onViewUserLog={(selected) =>
                  navigate(
                    `/reports?report=hendelseslogg&phone=${encodeURIComponent(selected.mobileNumber || '')}&user=${encodeURIComponent(
                      selected.lastUserId || selected.lastEmail || selected.lastName
                    )}`
                  )
                }
                onBlaalys={handleBlaalys}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {filteredPhones.map((phone) => (
            <PhoneRow
              key={phone.phoneId}
              phone={phone}
              groups={groupsByPhoneId[phone.phoneId] || []}
              onOpenMenu={(event, selected) => {
                setMenuAnchor(event.currentTarget);
                setMenuPhone(selected);
              }}
              onShowKeys={setKeysPhone}
              onViewLog={(selected) =>
                navigate(
                  `/reports?report=hendelseslogg&phone=${encodeURIComponent(selected.mobileNumber || '')}`
                )
              }
            />
          ))}
        </Stack>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuPhone(null);
        }}
      >
        {isManualPhoneLogonEnabled() && menuPhone && !menuPhone.isLoggedIn && (
          <MenuItem
            onClick={() => openManualLogonDialog(menuPhone)}
            disabled={Boolean(sessionSubmitting)}
          >
            {sessionSubmitting === menuPhone.phoneId ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <Login fontSize="small" sx={{ mr: 1 }} />
            )}
            Marker som innlogget
          </MenuItem>
        )}
        {isManualPhoneLogoffEnabled() &&
          menuPhone &&
          (menuPhone.isLoggedIn || (groupsByPhoneId[menuPhone.phoneId] || []).length > 0) && (
          <MenuItem
            onClick={() => handleManualLogoff(menuPhone)}
            disabled={Boolean(sessionSubmitting)}
          >
            {sessionSubmitting === menuPhone.phoneId ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <Logout fontSize="small" sx={{ mr: 1 }} />
            )}
            Marker som avlogget
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menuPhone) {
              setKeysPhone(menuPhone);
            }
            setMenuAnchor(null);
            setMenuPhone(null);
          }}
        >
          <VpnKey fontSize="small" sx={{ mr: 1 }} />
          Vis nøkler
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuPhone) {
              navigate(
                `/reports?report=hendelseslogg&phone=${encodeURIComponent(menuPhone.mobileNumber || '')}`
              );
            }
            setMenuAnchor(null);
            setMenuPhone(null);
          }}
        >
          <History fontSize="small" sx={{ mr: 1 }} />
          Hendelseslogg
        </MenuItem>
        {menuPhone?.isLoggedIn && (
          <MenuItem
            onClick={() => {
              // Phone-only: Unloc-åpninger skal vises også uten koblet sesjonsbruker.
              // (Brukerfilter på opencare-gui / navn uten treff tømte listen.)
              navigate(
                `/reports?report=hendelseslogg&phone=${encodeURIComponent(menuPhone.mobileNumber || '')}`
              );
              setMenuAnchor(null);
              setMenuPhone(null);
            }}
          >
            <Person fontSize="small" sx={{ mr: 1 }} />
            Logg for pålogget telefon
          </MenuItem>
        )}
        <MenuItem onClick={() => menuPhone && openEditDialog(menuPhone)}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Rediger
        </MenuItem>
        <MenuItem
          onClick={() => menuPhone && handleSyncPhoneFromUnloc(menuPhone)}
          disabled={Boolean(syncingPhoneId) || syncing}
        >
          {syncingPhoneId === menuPhone?.phoneId ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : (
            <Sync fontSize="small" sx={{ mr: 1 }} />
          )}
          Hent fra Unloc
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuPhone) {
              setAddGroupPhone(menuPhone);
            }
            setMenuAnchor(null);
            setMenuPhone(null);
          }}
        >
          <Group fontSize="small" sx={{ mr: 1 }} />
          Legg til i gruppe…
        </MenuItem>
        <MenuItem onClick={() => menuPhone && handleDeactivate(menuPhone)}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Deaktiver
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPhone ? 'Rediger telefon' : 'Ny telefon'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Navn"
            value={formData.name}
            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
            disabled={saving}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Mobilnummer"
            placeholder="+4791750101"
            value={formData.mobileNumber}
            onChange={(event) => setFormData({ ...formData, mobileNumber: event.target.value })}
            disabled={saving}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.blaalys}
                onChange={(event) => setFormData({ ...formData, blaalys: event.target.checked })}
                disabled={saving}
              />
            }
            label="Blålystelefon (kan utløse alle låser)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.lagreNokler}
                onChange={(event) => setFormData({ ...formData, lagreNokler: event.target.checked })}
                disabled={saving}
              />
            }
            label="Lagre nøkler i Unloc-appen (varige nøkler)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Avbryt
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Lagrer…' : 'Lagre'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(logonDialogPhone)}
        onClose={closeManualLogonDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Marker som innlogget</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {logonDialogPhone
              ? `${logonDialogPhone.name} (${logonDialogPhone.mobileNumber})`
              : ''}
            . Samme som mobil innlogging: telefonen legges i default-tilgangsgrupper og får
            Unloc-nøkler.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            required
            label="Pålogget bruker"
            placeholder="Navn på ansatt"
            value={logonUserName}
            onChange={(event) => {
              setLogonUserName(event.target.value);
              if (logonUserError) {
                setLogonUserError('');
              }
            }}
            error={Boolean(logonUserError)}
            helperText={logonUserError || 'Hvem som faktisk bruker telefonen nå'}
            disabled={Boolean(sessionSubmitting)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleManualLogonConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeManualLogonDialog} disabled={Boolean(sessionSubmitting)}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleManualLogonConfirm}
            disabled={Boolean(sessionSubmitting)}
            startIcon={
              sessionSubmitting === logonDialogPhone?.phoneId ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Login />
              )
            }
          >
            Logg på
          </Button>
        </DialogActions>
      </Dialog>

      <AddToAccessGroupDialog
        open={Boolean(addGroupPhone)}
        onClose={() => setAddGroupPhone(null)}
        title={`Legg ${addGroupPhone?.name || 'telefon'} til i gruppe`}
        addToGroup={(groupId) =>
          dataverseService.accessGroupService.addPhoneToGroup(groupId, addGroupPhone.phoneId)
        }
        onAdded={loadPhones}
      />

      <PhoneKeysDialog
        open={Boolean(keysPhone)}
        phone={keysPhone}
        onClose={() => setKeysPhone(null)}
      />
    </Box>
  );
};

export default PhonesPage;
